import { useTranslation } from 'next-i18next'
import { FocusEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { DEFAULT_MAX_PER_SIZE } from '@src/components/cart/b2b/b2b.constants'
import { NumericQtyInput } from '@src/components/cart/b2b/NumericQtyInput'
import { renderB2bInlineErrorMessage } from '@src/components/cart/b2b/render-b2b-inline-error-message'
import { isOneSizeProduct } from '@src/components/cart/b2b/utils/buildSizeBreakdown.util'
import { Skeleton } from '@src/quince-components/src/atoms/skeleton'
import useStorefrontModeContext from '@src/quince-site-common/src/contexts/storefront-mode-context'
import Product from '@src/quince-site-common/src/domains/Product'
import ProductOptionValue, { ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import { IProductPurchaseInfo } from '@src/quince-site-common/src/domains/ProductPurchaseInfo'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import { VARIANT_QUANTITY_MAP_QUERY_PARAM } from '@src/quince-site-common/src/organisms/b2b/self-serve/customizationPage/types'

import styles from './b2b-size-quantity-grid.module.scss'

// Matches the .grid quantity box height (4.8rem) so the loading shimmer occupies the same space.
const QTY_BOX_SKELETON_HEIGHT = 48

interface B2BSelfServeSizeQuantityGridProps {
  optionValueList: ProductOptionValue[]
  product?: Product
  selectedOptions?: ProductVariantOption[]
  productPurchaseInfo?: IProductPurchaseInfo
}

export default function B2BSelfServeSizeQuantityGrid({
  optionValueList,
  product,
  selectedOptions,
  productPurchaseInfo,
}: B2BSelfServeSizeQuantityGridProps) {
  const {
    storeProgramResponse,
    bulkOrderInputStateByVariantId,
    setBulkOrderInputStateByVariantId,
    isCustomizationEnabled,
  } = useStorefrontModeContext()
  const { t } = useTranslation('common')
  const [oosClickErrorMessage, setOosClickErrorMessage] = useState('')
  const [activeOosLineItemId, setActiveOosLineItemId] = useState<string | null>(null)
  const [customizationToggleErrors, setCustomizationToggleErrors] = useState<string[]>([])
  const prevCustomizationEnabledRef = useRef(isCustomizationEnabled)
  const hideSizeLabels = useMemo(() => isOneSizeProduct(optionValueList), [optionValueList])
  // Inventory (productPurchaseInfo) loads async. Until it arrives every size resolves to 0 stock,
  // which would render the quantity boxes crossed-out on first paint (SE-1411). Show a shimmer
  // placeholder while loading and only apply the real out-of-stock state once inventory is known.
  const isInventoryLoading = Boolean(product) && productPurchaseInfo == undefined

  const getInventoryErrorMessage = useCallback(
    (availableInventory: number, sizeLabel: string, quantity: number): string => {
      const MAX_QUANTITY_ALLOWED = storeProgramResponse?.storeProgramConfig?.maxItemsPerLineItem ?? DEFAULT_MAX_PER_SIZE

      if (availableInventory <= 0) return t('size_out_of_stock', { var1: sizeLabel })

      if (quantity > MAX_QUANTITY_ALLOWED) return t('max_per_size_contact_error', { var1: MAX_QUANTITY_ALLOWED })

      if (quantity > availableInventory)
        return t('inventory_limit_single', { var1: availableInventory, var2: sizeLabel })

      return ''
    },
    [storeProgramResponse?.storeProgramConfig?.maxItemsPerLineItem, t],
  )

  const sizeGridData = useMemo(() => {
    if (!product)
      return {
        sizeOptions: [],
        inventoryMap: {},
        outOfStockSizes: [],
      }

    const selectedColorOptions = (selectedOptions ?? []).filter(option => option.name == ProductOptionName.Color)
    const variantsForSelectedOptions = productHelper.getVariantsForOptions(product, selectedColorOptions)
    const sizeToVariantMap = new Map<string, (typeof variantsForSelectedOptions)[number]>()

    variantsForSelectedOptions.forEach(variant => {
      const sizeValue = productHelper.getOptionValueByNameInVariant(variant, ProductOptionName.Size)
      if (sizeValue && !sizeToVariantMap.has(sizeValue)) {
        sizeToVariantMap.set(sizeValue, variant)
      }
    })

    const inventoryMap: Record<string, number> = {}
    const outOfStockSizes: string[] = []

    const sizeOptions = optionValueList.map(optionValue => {
      const variant = sizeToVariantMap.get(optionValue.value)
      const lineItemId = String(variant?.id)

      const svDetails = variant ? productPurchaseInfo?.svidVsSvDetails?.[variant?.id] : undefined
      const variantSupportsCustomization = variant?.customization != undefined
      const availableInventory = svDetails
        ? isCustomizationEnabled
          ? variantSupportsCustomization
            ? (svDetails.availableForCustomization ?? 0)
            : 0
          : svDetails.quantityAvailable
        : 0

      inventoryMap[lineItemId] = availableInventory

      if (availableInventory === 0) {
        outOfStockSizes.push(optionValue.value)
      }

      return {
        lineItemId,
        sizeLabel: optionValue.value,
      }
    })

    return {
      sizeOptions,
      inventoryMap,
      outOfStockSizes,
    }
  }, [optionValueList, product, productPurchaseInfo, selectedOptions, isCustomizationEnabled])

  const errorMessages = useMemo(() => {
    return Object.values(bulkOrderInputStateByVariantId ?? {})
      .filter(state => state.errorMessage)
      .map(state => state.errorMessage)
  }, [bulkOrderInputStateByVariantId])

  const displayedErrorMessages = useMemo(() => {
    const combined = [...errorMessages, ...customizationToggleErrors]
    if (oosClickErrorMessage) combined.push(oosClickErrorMessage)
    // Collapse identical messages so the same text (e.g. "Max N per size") isn't stacked
    // once per affected size; distinct per-variant messages are still shown individually.
    return Array.from(new Set(combined))
  }, [errorMessages, oosClickErrorMessage, customizationToggleErrors])

  const clearCustomizationToggleErrors = useCallback(() => {
    setCustomizationToggleErrors(prev => (prev.length === 0 ? prev : []))
  }, [])

  // Reset entered quantities and errors on mount and whenever the selected color changes.
  useEffect(() => {
    setBulkOrderInputStateByVariantId?.({})
    setOosClickErrorMessage('')
    setActiveOosLineItemId(null)
    clearCustomizationToggleErrors()
  }, [selectedOptions, setBulkOrderInputStateByVariantId, clearCustomizationToggleErrors])

  // One-time hydrate of entered qty from vq (browser-back), reading window.location since router.query lags until router.isReady.
  // Self-terminating: the strip below removes vq from the URL, so any later run reads an empty map and bails.
  useEffect(() => {
    // Inventory loads async; defer hydration until it arrives, else OOS effects drop the qty.
    if (productPurchaseInfo == undefined) return

    const searchParams = new URLSearchParams(window.location.search)
    const vqString = searchParams.get(VARIANT_QUANTITY_MAP_QUERY_PARAM)
    const vqMap = vqString
      ? vqString.split(',').reduce<Record<string, number>>((acc, pair) => {
          const [variantId, quantity] = pair.trim().split(':')
          if (!variantId) return acc
          const parsedQty = Number(quantity)
          if (Number.isFinite(parsedQty) && parsedQty > 0) acc[variantId] = parsedQty
          return acc
        }, {})
      : {}
    if (Object.keys(vqMap).length === 0) return

    const restoredState = sizeGridData.sizeOptions.reduce<Record<string, { quantity: number; sizeLabel: string }>>(
      (acc, { lineItemId, sizeLabel }) => {
        const quantity = vqMap[lineItemId]
        if (quantity != null && quantity > 0) acc[lineItemId] = { quantity, sizeLabel }
        return acc
      },
      {},
    )
    setBulkOrderInputStateByVariantId?.(restoredState)

    // Strip vq via history.replaceState (not router.replace): sync and won't perturb selectedOptions to re-trip the reset above.
    searchParams.delete(VARIANT_QUANTITY_MAP_QUERY_PARAM)
    const queryString = searchParams.toString()
    const urlWithoutVq = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname
    window.history.replaceState(window.history.state, '', urlWithoutVq)
  }, [productPurchaseInfo, setBulkOrderInputStateByVariantId])

  // When the user checks/unchecks the customization checkbox, surface errors for any
  // previously-entered sizes that have no inventory in the new mode (their entered qty is
  // about to be dropped by the inventory sync effect below).
  useEffect(() => {
    if (prevCustomizationEnabledRef.current === isCustomizationEnabled) return
    prevCustomizationEnabledRef.current = isCustomizationEnabled

    const affectedMessages: string[] = []
    Object.entries(bulkOrderInputStateByVariantId ?? {}).forEach(([lineItemId, state]) => {
      const availableInventory = sizeGridData.inventoryMap[lineItemId]
      if (availableInventory !== undefined && availableInventory <= 0) {
        affectedMessages.push(getInventoryErrorMessage(0, state.sizeLabel, 1))
      }
    })

    setCustomizationToggleErrors(prev => {
      if (prev.length === 0 && affectedMessages.length === 0) return prev
      return affectedMessages
    })
  }, [isCustomizationEnabled, sizeGridData, bulkOrderInputStateByVariantId, getInventoryErrorMessage])

  useEffect(() => {
    if (activeOosLineItemId && (sizeGridData.inventoryMap[activeOosLineItemId] ?? 0) > 0) {
      setOosClickErrorMessage('')
      setActiveOosLineItemId(null)
    }
  }, [sizeGridData, activeOosLineItemId])

  useEffect(() => {
    if (!bulkOrderInputStateByVariantId) return

    let hasChanges = false
    let updatedState = { ...bulkOrderInputStateByVariantId }

    Object.entries(bulkOrderInputStateByVariantId).forEach(([lineItemId, state]) => {
      const availableInventory = sizeGridData.inventoryMap[lineItemId]
      if (availableInventory == undefined) return

      if (availableInventory <= 0) {
        hasChanges = true
        const { [lineItemId]: _, ...remaining } = updatedState
        updatedState = remaining
        return
      }

      const newErrorMessage = getInventoryErrorMessage(availableInventory, state.sizeLabel, state.quantity)
      const currentErrorMessage = state.errorMessage ?? ''

      if (newErrorMessage !== currentErrorMessage) {
        hasChanges = true
        if (newErrorMessage) {
          updatedState[lineItemId] = { ...state, errorMessage: newErrorMessage }
        } else {
          const { errorMessage: _, ...rest } = state
          updatedState[lineItemId] = rest
        }
      }
    })

    if (hasChanges) {
      setBulkOrderInputStateByVariantId?.(updatedState)
    }
  }, [bulkOrderInputStateByVariantId, sizeGridData, getInventoryErrorMessage, setBulkOrderInputStateByVariantId])

  // Clear OOS error + active highlight and customization-toggle errors when focus lands on another
  // size input box. Keeps the errors intact when clicking outside the grid (e.g. customization checkbox).
  const handleSectionFocus = (event: FocusEvent<HTMLElement>) => {
    if ((event.target as HTMLElement).tagName !== 'INPUT') return
    if (activeOosLineItemId) {
      setActiveOosLineItemId(null)
      setOosClickErrorMessage('')
    }
    clearCustomizationToggleErrors()
  }

  return (
    <section className={styles.sectionWrapper} onFocus={handleSectionFocus}>
      <div className={styles.grid}>
        {sizeGridData.sizeOptions.map(option => {
          const { lineItemId, sizeLabel } = option
          const hasStoredQty = bulkOrderInputStateByVariantId?.[lineItemId] !== undefined
          const quantity = bulkOrderInputStateByVariantId?.[lineItemId]?.quantity ?? 0
          const hasError = Boolean(bulkOrderInputStateByVariantId?.[lineItemId]?.errorMessage)
          const isOutOfStock = sizeGridData.outOfStockSizes.includes(sizeLabel)

          if (isInventoryLoading) {
            return (
              <div key={lineItemId} className={styles.sizeItemWrapper}>
                <Skeleton fullWidth height={QTY_BOX_SKELETON_HEIGHT} variant="NONE" />
                {!hideSizeLabels && <p className={styles.sizeLabel}>{sizeLabel}</p>}
              </div>
            )
          }

          return (
            <div key={lineItemId} className={styles.sizeItemWrapper}>
              <NumericQtyInput
                value={quantity}
                variant="grid"
                // Blank until a quantity is entered; once entered (including a deliberate 0)
                // keep it visible. notifyOnCommit lets a typed 0 register so the box shows "0".
                emptyWhenZero={!hasStoredQty}
                notifyOnCommit
                hasError={hasError}
                isOutOfStock={isOutOfStock}
                isOosActive={isOutOfStock && activeOosLineItemId === lineItemId}
                onOosClick={() => {
                  setActiveOosLineItemId(lineItemId)
                  setOosClickErrorMessage(getInventoryErrorMessage(0, sizeLabel, 1))
                  clearCustomizationToggleErrors()
                }}
                // Clearing the box drops the entry so it goes blank (instead of reverting to 0).
                onClear={() => {
                  setBulkOrderInputStateByVariantId?.(prevState => {
                    const { [lineItemId]: _removed, ...remainingState } = prevState ?? {}
                    return remainingState
                  })
                }}
                onChange={(newQty: number | string) => {
                  setOosClickErrorMessage('')
                  setActiveOosLineItemId(null)
                  clearCustomizationToggleErrors()
                  setBulkOrderInputStateByVariantId?.(prevState => {
                    // 0 is a valid, retained entry. Only drop the size for non-finite/negative input.
                    const parsedQty = Number(newQty)
                    if (!Number.isFinite(parsedQty) || parsedQty < 0) {
                      const { [lineItemId]: _removed, ...remainingState } = prevState ?? {}
                      return remainingState
                    }

                    return {
                      ...(prevState ?? {}),
                      [lineItemId]: {
                        quantity: parsedQty,
                        sizeLabel,
                      },
                    }
                  })
                }}
              />
              {!hideSizeLabels && <p className={styles.sizeLabel}>{sizeLabel}</p>}
            </div>
          )
        })}
      </div>

      {displayedErrorMessages.map((errorMessage, index) => (
        <div key={index} className={styles.errorBlock}>
          <p className={styles.errorMessage}>
            {renderB2bInlineErrorMessage(errorMessage, styles.contactLink as string)}
          </p>
        </div>
      ))}
    </section>
  )
}
